import * as React from "react";

import { Loader } from "@editor/components/common/Loader";
import { routes } from "@editor/utils/router";
import { trpc } from "@editor/utils/trpc";

import {
  generatePath,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";

// Mapping from old routes to new shops routes
const ROUTE_MAPPINGS: Record<string, string> = {
  // Workspace analytics routes
  "/workspace/:workspaceId/analytics": "/shops/:projectId/analytics",
  [routes.analytics.home]: routes.shops.analytics.home,
  [routes.analytics.overview]: routes.shops.analytics.overview,
  [routes.analytics.pageDetails]: routes.shops.analytics.pageDetails,
  [routes.analytics.insights]: routes.shops.analytics.insights,

  // Workspace experiments routes
  [routes.workspace.experiments.list]: routes.shops.experiments.list,
  [routes.workspace.experiments.details]: routes.shops.experiments.details,
  [routes.workspace.experiments.results]: routes.shops.experiments.results,
  [routes.workspace.experiments.settings]:
    routes.shops.settings.shops.abtesting,
  [routes.workspace.experiments.edit]: routes.shops.experiments.edit,

  // Workspace projects -> site
  [routes.workspace.projects]: routes.shops.site,

  // Workspace settings routes
  "/workspace/:workspaceId/members": routes.shops.settings.workspace.members,
  [routes.workspace.billing]: routes.shops.settings.workspace.billing,

  // Workspace integrations
  [routes.workspace.integrations.list]:
    routes.shops.settings.shops.integrations,
  [routes.workspace.integrations.shopify]:
    routes.shops.settings.shops.connections,

  // Workspace agnostic routes
  "/analytics": "/shops/:projectId/analytics",
  "/analytics/home": routes.shops.analytics.home,
  "/analytics/overview": routes.shops.analytics.overview,
  "/analytics/deep-dive": routes.shops.analytics.pageDetails,
  "/analytics/insights": routes.shops.analytics.insights,
  "/abtesting": routes.shops.experiments.list,

  // Profile Settings
  "/settings/profile": "/shops/:projectId/settings/profile",
  "/settings/security": "/shops/:projectId/settings/profile",

  // All projects
  "/project": routes.shops.site,
  "/project/new": routes.shops.site,

  // "/home": routes.shops.site,
};

// Function to match current path against patterns and return the shops route
function getShopsRedirectPath(currentPath: string): string | null {
  for (const [pattern, targetPath] of Object.entries(ROUTE_MAPPINGS)) {
    // Convert pattern to regex for exact matching
    const patternRegex = new RegExp(
      `^${pattern.replace(/:[^/]+/g, "([^/]+)")}$`,
    );

    const match = patternRegex.exec(currentPath);
    if (match) {
      // Extract parameter names from the pattern
      const paramNames =
        pattern.match(/:([^/]+)/g)?.map((p) => p.slice(1)) ?? [];
      const paramValues = match.slice(1);

      // Create a mapping of parameter names to values
      const params: Record<string, string> = {};
      paramNames.forEach((name, index) => {
        const value = paramValues[index];
        if (value !== undefined) {
          params[name] = value;
        }
      });

      // Replace parameters in target pattern
      let result = targetPath;
      Object.entries(params).forEach(([name, value]) => {
        result = result.replace(`:${name}`, value);
      });

      return result;
    }
  }

  return null;
}

// NOTE (Craig 2025-06-11): This will handle redirecting old routes to the new shops UI.
export const ShopsRedirectRoute: React.FC = () => {
  const { workspaceId } = useParams();
  const location = useLocation();

  const { data: projectData, isLoading: projectIsLoading } =
    trpc.project.getDefaultProjectId.useQuery({
      workspaceId: workspaceId ?? undefined,
    });

  if (window.localStorage.getItem("REPLO_IS_E2E_TEST") === "true") {
    return <Outlet />;
  }

  if (projectIsLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader />
      </div>
    );
  }

  const projectId = projectData?.projectId;

  if (projectId) {
    // Try to map the current path to a shops route
    const shopsPath = getShopsRedirectPath(location.pathname);
    if (shopsPath) {
      return <Navigate to={generatePath(shopsPath, { projectId })} replace />;
    }
  }

  // If no mapping exists or no project ID, render the regular route
  return <Outlet />;
};
